// This Modal is for bulk assignment of trainings to volunteers and opens on Admin Trainings page

import React, { useState, useEffect, useMemo } from 'react'
import { BulkTrainingAssignmentFormMetaProps, VolunteerFiltersProps, Volunteer, Training, Flag, BaseFilterUpdate, Event, Area, ShiftWithDuty, Duty, TrainingAssignmentMode } from '../data/types'
import { VolunteerTrainingTableMeta, GenderOptions, IVPositionFilterOptions, AllLanguages, DEFAULT_FILTERS, AVAILABILITY_OPTIONS } from '../data'
import { Button, Modal, Card } from './ui'
import { API_BASE_URL } from '../data'
import { VolunteerFilters } from '../components'
import VolunteerTable from './VolunteerTable'
import { ChevronLeftIcon, ChevronRightIcon, UsersIcon, CalendarIcon, LocationIcon, ClockIcon, BriefcaseIcon, PersonIcon } from '../icons'
import { EU_JAMAT_KHANA_OPTIONS } from '../data/meta'
import { getShiftDisplayName, sortByName, sortShifts } from '../utils/sorting'


type Props = {
    meta: BulkTrainingAssignmentFormMetaProps
    isOpen: boolean
    training: Training
    onClose: () => void
}

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

type PreviewData = {
    total_volunteers: number
    already_assigned: number
    will_be_assigned: number
} | null

const ITEMS_PER_PAGE = 5

export const BulkTrainingAssignmentModal = (props: Props) => {

    const { meta, isOpen, training, onClose } = props
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean | null>(false)
    const [submitting, setSubmitting] = useState(false)
    const [previewing, setPreviewing] = useState(false)
    const [previewData, setPreviewData] = useState<PreviewData>(null)

    // Assignment mode
    const [assignmentMode, setAssignmentMode] = useState<TrainingAssignmentMode>('all')

    // Data for selectors
    const [events, setEvents] = useState<Event[]>([])
    const [areas, setAreas] = useState<Area[]>([])
    const [shifts, setShifts] = useState<ShiftWithDuty[]>([])
    const [duties, setDuties] = useState<Duty[]>([])

    // Selected items
    const [selectedEventIds, setSelectedEventIds] = useState<number[]>([])
    const [selectedAreaIds, setSelectedAreaIds] = useState<number[]>([])
    const [selectedShiftIds, setSelectedShiftIds] = useState<number[]>([])
    const [selectedDutyIds, setSelectedDutyIds] = useState<number[]>([])

    // Lead inclusion options
    const [includeEventLeads, setIncludeEventLeads] = useState(true)
    const [includeAreaLeads, setIncludeAreaLeads] = useState(true)
    const [includeShiftLeads, setIncludeShiftLeads] = useState(true)

    // Manual selection state
    const [Volunteers, setVolunteers] = useState<Volunteer[]>([])
    const [selectedVolunteers, setSelectedVolunteers] = useState<Volunteer[]>([])
    const [Flags, setFlags] = useState<Flag[]>([])
    const [filters, setFilters] = useState<VolunteerFiltersProps>(DEFAULT_FILTERS)
    const [loadingPage, setLoadingPage] = useState<number>(1)
    const [pagination, setPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    })

    // Fetch events and duties on modal open
    useEffect(() => {
        if (!isOpen) return

        const fetchInitialData = async () => {
            try {
                const eventsResponse = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (eventsResponse.ok) {
                    const eventsData = await eventsResponse.json()
                    setEvents(Array.isArray(eventsData) ? eventsData : eventsData.value || [])
                }

                const dutiesResponse = await fetch(`${API_BASE_URL}/duty`, { credentials: 'include' })
                if (dutiesResponse.ok) {
                    const dutiesData = await dutiesResponse.json()
                    setDuties(Array.isArray(dutiesData) ? dutiesData : dutiesData.value || [])
                }
            } catch (err) {
                console.error('Error fetching initial data:', err)
            }
        }

        fetchInitialData()
    }, [isOpen])

    // Fetch areas when events are selected
    useEffect(() => {
        if (selectedEventIds.length === 0) {
            setAreas([])
            setSelectedAreaIds([])
            return
        }

        const fetchAreas = async () => {
            try {
                const allAreas: Area[] = []
                for (const eventId of selectedEventIds) {
                    const response = await fetch(`${API_BASE_URL}/area/${eventId}`, { credentials: 'include' })
                    if (response.ok) {
                        const result = await response.json()
                        let data = result.value || result
                        // Handle double-nested arrays
                        if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                            data = data[0]
                        }
                        const eventAreas = Array.isArray(data) ? data : []
                        allAreas.push(...eventAreas)
                    }
                }
                setAreas(allAreas)
            } catch (err) {
                console.error('Error fetching areas:', err)
            }
        }

        fetchAreas()
    }, [selectedEventIds])

    // Fetch shifts when areas are selected
    useEffect(() => {
        if (selectedAreaIds.length === 0) {
            setShifts([])
            setSelectedShiftIds([])
            return
        }

        const fetchShifts = async () => {
            try {
                const allShifts: ShiftWithDuty[] = []
                for (const areaId of selectedAreaIds) {
                    const response = await fetch(`${API_BASE_URL}/shift/${areaId}`, { credentials: 'include' })
                    if (response.ok) {
                        const result = await response.json()
                        let data = result.value || result
                        // Handle double-nested arrays
                        if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                            data = data[0]
                        }
                        const areaShifts = Array.isArray(data) ? data : []
                        allShifts.push(...areaShifts)
                    }
                }
                setShifts(allShifts)
            } catch (err) {
                console.error('Error fetching shifts:', err)
            }
        }

        fetchShifts()
    }, [selectedAreaIds])

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setError(null)
            setAssignmentMode('all')
            setSelectedEventIds([])
            setSelectedAreaIds([])
            setSelectedShiftIds([])
            setSelectedDutyIds([])
            setIncludeEventLeads(true)
            setIncludeAreaLeads(true)
            setIncludeShiftLeads(true)
            setPreviewData(null)
            setSelectedVolunteers([])
            setVolunteers([])
            setFilters(DEFAULT_FILTERS)
        }
    }, [isOpen])

    // Clear preview when selections change
    useEffect(() => {
        setPreviewData(null)
    }, [assignmentMode, selectedEventIds, selectedAreaIds, selectedShiftIds, selectedDutyIds, includeEventLeads, includeAreaLeads, includeShiftLeads])


    const buildQueryString = (page: number, filters: BaseFilterUpdate) => {
        const params = new URLSearchParams()
        params.append('per_page', ITEMS_PER_PAGE.toString())
        params.append('page', page.toString())
        params.append('current-training', training.id.toString())

        // Add all filter parameters
        if (filters.name) params.append('name', filters.name)
        if (filters.email) params.append('email', filters.email)
        if (filters.phone_number) params.append('phone_number', filters.phone_number)
        if (filters.id) params.append('id', filters.id)
        if (filters.country) params.append('country', filters.country)
        if (filters.country_of_birth) params.append('country_of_birth', filters.country_of_birth)
        if (filters.family_origin) params.append('family_origin', filters.family_origin)
        if (filters.jamat_khana) params.append('jamat_khana', filters.jamat_khana)
        if (filters.iv_position) params.append('iv_position', filters.iv_position)
        if (filters.age) params.append('age', filters.age.toString())
        if (filters.age_start) params.append('age_start', filters.age_start.toString())
        if (filters.age_end) params.append('age_end', filters.age_end.toString())

        // Boolean filters - only send if not null
        if (filters.ismaili !== null && filters.ismaili !== undefined) {
            params.append('ismaili', filters.ismaili.toString())
        }
        if (filters.registered_volunteer !== null && filters.registered_volunteer !== undefined) {
            params.append('registered_volunteer', filters.registered_volunteer.toString())
        }
        if (filters.profile_submitted !== null && filters.profile_submitted !== undefined) {
            params.append('profile_submitted', filters.profile_submitted.toString())
        }
        if (filters.approved_volunteer !== null && filters.approved_volunteer !== undefined) {
            params.append('approved_volunteer', filters.approved_volunteer.toString())
        }
        if (filters.eligible_for_duty !== null && filters.eligible_for_duty !== undefined) {
            params.append('eligible_for_duty', filters.eligible_for_duty.toString())
        }
        if (filters.multi_faith_family !== null && filters.multi_faith_family !== undefined) {
            params.append('multi_faith_family', filters.multi_faith_family.toString())
        }
        if (filters.has_support_needs !== null && filters.has_support_needs !== undefined) {
            params.append('has_support_needs', filters.has_support_needs.toString())
        }
        if (filters.volunteer_for_visit !== null && filters.volunteer_for_visit !== undefined) {
            params.append('volunteer_for_visit', filters.volunteer_for_visit.toString())
        }

        // Array filters - send as JSON
        if (filters.genders && filters.genders.length > 0) {
            params.append('genders', JSON.stringify(filters.genders))
        }
        if (filters.service_record && filters.service_record.length > 0) {
            params.append('service_record', JSON.stringify(filters.service_record))
        }
        if (filters.clusters && filters.clusters.length > 0) {
            params.append('clusters', JSON.stringify(filters.clusters))
        }
        if (filters.languages && filters.languages.length > 0) {
            params.append('languages', JSON.stringify(filters.languages))
        }
        if (filters.tags && filters.tags.length > 0) {
            params.append('tags', JSON.stringify(filters.tags.map(t => t.id)))
        }

        if (filters.trainings && filters.trainings.length > 0) {
            params.append('trainings', JSON.stringify(filters.trainings.map(t => t.id)))
            // Add training filter type (defaults to 'at_least_one' if not specified)
            if (filters.training_filter_type) {
                params.append('training_filter_type', filters.training_filter_type)
            }
        }
        if (filters.availability && filters.availability.length > 0) {
            params.append('availability', JSON.stringify(filters.availability))
            if (filters.availability_filter_type) {
                params.append('availability_filter_type', filters.availability_filter_type)
            }
        }

        // String filters
        if (filters.iiuk_registeration) {
            params.append('iiuk_registeration', filters.iiuk_registeration)
        }

        // Event assignment filter
        if (filters.event_assignment_type && filters.event_assignment_events && filters.event_assignment_events.length > 0) {
            params.append('event_assignment_type', filters.event_assignment_type)
            params.append('event_assignment_events', JSON.stringify(filters.event_assignment_events.map(e => e.id)))
        }

        // Core team assignment filter
        if (filters.core_team_assignment_type) {
            params.append('core_team_assignment_type', filters.core_team_assignment_type)
        }

        return params.toString()
    }

    const fetchVolunteers = async (page: number, filters: BaseFilterUpdate) => {
        setLoadingPage(page)
        setLoading(true)
        setError(null)

        try {
            const queryString = buildQueryString(page, filters)
            const response = await fetch(`${API_BASE_URL}/volunteer?${queryString}`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const result = await response.json()

            // Handle paginated response structure
            const volunteersData = result.data || []

            // Normalize boolean fields
            const normalized = volunteersData.map((v: any) => ({
                ...v,
                eligible_for_duty: v.eligible_for_duty === 1,
                ismaili: v.ismaili === 1,
                registered_volunteer: v.registered_volunteer === 1,
                multi_faith_family: v.multi_faith_family === 1,
                volunteer_for_visit: v.volunteer_for_visit !== null && v.volunteer_for_visit !== undefined ? Number(v.volunteer_for_visit) : v.volunteer_for_visit
            }))

            setVolunteers(normalized)
            setPagination({
                current_page: result.current_page || 1,
                last_page: result.last_page || 1,
                per_page: result.per_page || ITEMS_PER_PAGE,
                total: result.total || 0
            })
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching volunteers')
        } finally {
            setLoading(false)
        }
    }

    const fetchAdditionalTags = async () => {
        // Try Fetching all Flags
        try {
            const response = await fetch(`${API_BASE_URL}/flag`, { credentials: 'include' })
            if (response.ok) {
                const result = await response.json()
                setFlags(result)
            }
        } catch (err) {
            console.error('Error fetching flags:', err)
        }
    }

    // Load volunteers when manual mode is selected
    useEffect(() => {
        if (assignmentMode === 'manual' && isOpen && Volunteers.length === 0) {
            fetchAdditionalTags()
            fetchVolunteers(1, filters)
        }
    }, [assignmentMode, isOpen])


    const handleFiltersChange = (update: BaseFilterUpdate) => {
        const newFilters = { ...filters, ...update }
        setFilters(newFilters)
        // Reset to page 1 when filters change
        fetchVolunteers(1, newFilters)
    }

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            fetchVolunteers(page, filters)
        }
    }

    const handlePreview = async () => {
        if (assignmentMode === 'manual') return

        setPreviewing(true)
        setError(null)

        try {
            const payload: any = {
                mode: assignmentMode,
                include_event_leads: includeEventLeads,
                include_area_leads: includeAreaLeads,
                include_shift_leads: includeShiftLeads
            }

            if (assignmentMode === 'event') {
                payload.event_ids = selectedEventIds
            } else if (assignmentMode === 'area') {
                payload.area_ids = selectedAreaIds
            } else if (assignmentMode === 'shift') {
                payload.shift_ids = selectedShiftIds
            } else if (assignmentMode === 'duty') {
                payload.duty_ids = selectedDutyIds
            }

            const response = await fetch(`${API_BASE_URL}/training/${training.id}/preview-targeted`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            })

            if (!response.ok) {
                throw new Error('Failed to preview assignment')
            }

            const data = await response.json()
            setPreviewData(data)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to preview assignment')
        } finally {
            setPreviewing(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!training) {
            setError("No training data available.")
            return
        }

        // Validation
        if (assignmentMode === 'manual' && selectedVolunteers.length === 0) {
            setError("At least one volunteer must be selected to assign training.")
            return
        }

        if (assignmentMode === 'event' && selectedEventIds.length === 0) {
            setError("Please select at least one event.")
            return
        }

        if (assignmentMode === 'area' && selectedAreaIds.length === 0) {
            setError("Please select at least one area.")
            return
        }

        if (assignmentMode === 'shift' && selectedShiftIds.length === 0) {
            setError("Please select at least one shift.")
            return
        }

        if (assignmentMode === 'duty' && selectedDutyIds.length === 0) {
            setError("Please select at least one duty type.")
            return
        }

        setSubmitting(true)
        setError(null)

        try {
            const payload: any = {
                mode: assignmentMode,
                include_event_leads: includeEventLeads,
                include_area_leads: includeAreaLeads,
                include_shift_leads: includeShiftLeads
            }

            if (assignmentMode === 'manual') {
                payload.volunteers = selectedVolunteers.map(v => v.csid)
            } else if (assignmentMode === 'event') {
                payload.event_ids = selectedEventIds
            } else if (assignmentMode === 'area') {
                payload.area_ids = selectedAreaIds
            } else if (assignmentMode === 'shift') {
                payload.shift_ids = selectedShiftIds
            } else if (assignmentMode === 'duty') {
                payload.duty_ids = selectedDutyIds
            }

            const response = await fetch(`${API_BASE_URL}/training/${training.id}/assign-targeted`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(payload)
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.message || 'Failed to assign training')
            }

            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : "Error while assigning training to volunteers.")
        } finally {
            setSubmitting(false)
        }
    }

    const canSubmit = useMemo(() => {
        if (submitting) return false
        if (assignmentMode === 'all') return true
        if (assignmentMode === 'event') return selectedEventIds.length > 0
        if (assignmentMode === 'area') return selectedAreaIds.length > 0
        if (assignmentMode === 'shift') return selectedShiftIds.length > 0
        if (assignmentMode === 'duty') return selectedDutyIds.length > 0
        if (assignmentMode === 'manual') return selectedVolunteers.length > 0
        return false
    }, [assignmentMode, selectedEventIds, selectedAreaIds, selectedShiftIds, selectedDutyIds, selectedVolunteers, submitting])

    const renderModeSelector = () => {
        const modes: { value: TrainingAssignmentMode; label: string; description: string; icon: React.ReactNode }[] = [
            { value: 'all', label: meta.modeAllVolunteers, description: meta.modeAllVolunteersDescription, icon: <UsersIcon className="w-5 h-5" /> },
            { value: 'event', label: meta.modeByEvent, description: meta.modeByEventDescription, icon: <CalendarIcon className="w-5 h-5" /> },
            { value: 'area', label: meta.modeByArea, description: meta.modeByAreaDescription, icon: <LocationIcon className="w-5 h-5" /> },
            { value: 'shift', label: meta.modeByShift, description: meta.modeByShiftDescription, icon: <ClockIcon className="w-5 h-5" /> },
            { value: 'duty', label: meta.modeByDuty, description: meta.modeByDutyDescription, icon: <BriefcaseIcon className="w-5 h-5" /> },
            { value: 'manual', label: meta.modeManual, description: meta.modeManualDescription, icon: <PersonIcon className="w-5 h-5" /> },
        ]

        return (
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {meta.assignmentModeLabel}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {modes.map(mode => (
                        <button
                            key={mode.value}
                            type="button"
                            onClick={() => setAssignmentMode(mode.value)}
                            className={`p-4 rounded-lg border-2 text-start transition-all ${assignmentMode === mode.value
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                                }`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <span className={assignmentMode === mode.value ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}>
                                    {mode.icon}
                                </span>
                                <span className={`font-medium ${assignmentMode === mode.value ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                    {mode.label}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{mode.description}</p>
                        </button>
                    ))}
                </div>
            </div>
        )
    }

    const renderEventSelector = () => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {meta.selectEventsLabel}
            </label>
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
                {sortByName(events).map(event => (
                    <label key={event.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                        <input
                            type="checkbox"
                            checked={selectedEventIds.includes(event.id)}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedEventIds([...selectedEventIds, event.id])
                                } else {
                                    setSelectedEventIds(selectedEventIds.filter(id => id !== event.id))
                                }
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{event.name}</span>
                    </label>
                ))}
                {events.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 p-2">No events available</p>
                )}
            </div>
        </div>
    )

    const renderAreaSelector = () => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {meta.selectAreasLabel}
            </label>
            {assignmentMode === 'area' && selectedEventIds.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">{meta.selectEventFirst}</p>
            ) : (
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
                    {sortByName(areas).map(area => (
                        <label key={area.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selectedAreaIds.includes(area.id)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedAreaIds([...selectedAreaIds, area.id])
                                    } else {
                                        setSelectedAreaIds(selectedAreaIds.filter(id => id !== area.id))
                                    }
                                }}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-900 dark:text-gray-100">{area.name}</span>
                        </label>
                    ))}
                    {areas.length === 0 && selectedEventIds.length > 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 p-2">No areas available</p>
                    )}
                </div>
            )}
        </div>
    )

    const renderShiftSelector = () => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {meta.selectShiftsLabel}
            </label>
            {selectedAreaIds.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">{meta.selectAreaFirst}</p>
            ) : (
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
                    {sortShifts(shifts).map(shift => (
                        <label key={shift.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selectedShiftIds.includes(shift.id)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedShiftIds([...selectedShiftIds, shift.id])
                                    } else {
                                        setSelectedShiftIds(selectedShiftIds.filter(id => id !== shift.id))
                                    }
                                }}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                                {getShiftDisplayName(shift)} ({shift.start_time} - {shift.end_time})
                            </span>
                        </label>
                    ))}
                    {shifts.length === 0 && selectedAreaIds.length > 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 p-2">No shifts available</p>
                    )}
                </div>
            )}
        </div>
    )

    const renderDutySelector = () => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {meta.selectDutiesLabel}
            </label>
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
                {duties.map(duty => (
                    <label key={duty.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                        <input
                            type="checkbox"
                            checked={selectedDutyIds.includes(duty.id)}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setSelectedDutyIds([...selectedDutyIds, duty.id])
                                } else {
                                    setSelectedDutyIds(selectedDutyIds.filter(id => id !== duty.id))
                                }
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{duty.name}</span>
                    </label>
                ))}
                {duties.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 p-2">No duty types available</p>
                )}
            </div>
        </div>
    )

    const renderLeadOptions = () => {
        if (assignmentMode === 'all' || assignmentMode === 'manual') return null

        return (
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {meta.includeLeadsLabel}
                </label>
                <div className="space-y-2">
                    {(assignmentMode === 'event' || assignmentMode === 'area' || assignmentMode === 'shift') && (
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeEventLeads}
                                onChange={(e) => setIncludeEventLeads(e.target.checked)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{meta.includeEventLeadsLabel}</span>
                        </label>
                    )}
                    {(assignmentMode === 'event' || assignmentMode === 'area' || assignmentMode === 'shift') && (
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeAreaLeads}
                                onChange={(e) => setIncludeAreaLeads(e.target.checked)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{meta.includeAreaLeadsLabel}</span>
                        </label>
                    )}
                    {(assignmentMode === 'event' || assignmentMode === 'area' || assignmentMode === 'shift' || assignmentMode === 'duty') && (
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeShiftLeads}
                                onChange={(e) => setIncludeShiftLeads(e.target.checked)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{meta.includeShiftLeadsLabel}</span>
                        </label>
                    )}
                </div>
            </div>
        )
    }

    const renderPreviewSection = () => {
        if (assignmentMode === 'manual') return null

        return (
            <div className="mb-4">
                <div className="flex items-center gap-4 mb-3">
                    <Button
                        type="button"
                        onClick={handlePreview}
                        disabled={previewing || (assignmentMode !== 'all' && !canSubmit)}
                        variant="outline"
                        color="primary"
                        size="sm"
                    >
                        {previewing ? meta.previewingButton : meta.previewButton}
                    </Button>
                </div>
                {previewData && (
                    <Card className="bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
                        <div className="p-4">
                            <h4 className="font-medium text-primary-900 dark:text-primary-100 mb-3">{meta.previewResultsTitle}</h4>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400">{meta.totalVolunteersLabel}</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{previewData.total_volunteers}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400">{meta.alreadyAssignedLabel}</p>
                                    <p className="text-2xl font-bold text-gray-500">{previewData.already_assigned}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400">{meta.willBeAssignedLabel}</p>
                                    <p className="text-2xl font-bold text-success-600 dark:text-success-400">{previewData.will_be_assigned}</p>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}
            </div>
        )
    }

    const renderManualSelection = () => {
        if (assignmentMode !== 'manual') return null

        return (
            <>
                {!loading && Volunteers.length > 0 && (
                    <div className="mb-4 text-gray-700 dark:text-gray-300">
                        Only volunteers who do not already have the training "<strong>{training?.name}</strong>" assigned are shown below.
                    </div>
                )}

                <VolunteerFilters
                    onChange={handleFiltersChange}
                    tagOptions={Flags}
                    languageOptions={AllLanguages}
                    serviceRecordOptions={IVPositionFilterOptions}
                    genderOptions={GenderOptions}
                    clusterOptions={EU_JAMAT_KHANA_OPTIONS}
                    availabilityOptions={AVAILABILITY_OPTIONS as unknown as string[]}
                    showRegisteredVolunteer={true}
                />

                <div className='dark:text-warn-50 mt-4 mb-4'>
                    <span>{`Number of Selected Volunteers: ${selectedVolunteers.length}`}</span>
                </div>

                <VolunteerTable
                    meta={VolunteerTrainingTableMeta}
                    data={Volunteers}
                    training={true}
                    selected={selectedVolunteers}
                    onSelectionChange={setSelectedVolunteers}
                />

                {pagination.last_page > 1 && renderPagination()}
            </>
        )
    }

    const renderPagination = () => (
        <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
                <Button
                    type="button"
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.current_page <= 1}
                    size="sm"
                >
                    <ChevronLeftIcon /><ChevronLeftIcon className="w-5 h-5 -ms-[22px]" /> First
                </Button>

                <Button
                    type="button"
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page <= 1}
                    size="sm"
                >
                    <ChevronLeftIcon /> Previous
                </Button>

                {(() => {
                    const pageNumbers = []
                    const maxPagesToShow = 5
                    let startPage = Math.max(1, pagination.current_page - Math.floor(maxPagesToShow / 2))
                    let endPage = Math.min(pagination.last_page, startPage + maxPagesToShow - 1)

                    if (endPage - startPage < maxPagesToShow - 1) {
                        startPage = Math.max(1, endPage - maxPagesToShow + 1)
                    }

                    if (startPage > 1) {
                        pageNumbers.push(
                            <Button
                                key={1}
                                type="button"
                                onClick={() => handlePageChange(1)}
                                size="sm"
                                variant="icon"
                            >
                                1
                            </Button>
                        )
                        if (startPage > 2) {
                            pageNumbers.push(
                                <span key="ellipsis1" className="px-2 text-gray-500">...</span>
                            )
                        }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                        pageNumbers.push(
                            <Button
                                key={i}
                                type="button"
                                onClick={() => handlePageChange(i)}
                                size="sm"
                                variant={i === loadingPage ? "solid" : "icon"}
                                color={i === loadingPage ? "primary" : "neutral"}
                            >
                                {i}
                            </Button>
                        )
                    }

                    if (endPage < pagination.last_page) {
                        if (endPage < pagination.last_page - 1) {
                            pageNumbers.push(
                                <span key="ellipsis2" className="px-2 text-gray-500">...</span>
                            )
                        }
                        pageNumbers.push(
                            <Button
                                key={pagination.last_page}
                                type="button"
                                onClick={() => handlePageChange(pagination.last_page)}
                                size="sm"
                                variant="icon"
                            >
                                {pagination.last_page}
                            </Button>
                        )
                    }

                    return pageNumbers
                })()}

                <Button
                    type="button"
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page >= pagination.last_page}
                    size="sm"
                >
                    Next <ChevronRightIcon />
                </Button>

                <Button
                    type="button"
                    onClick={() => handlePageChange(pagination.last_page)}
                    disabled={pagination.current_page >= pagination.last_page}
                    size="sm"
                >
                    Last <ChevronRightIcon /><ChevronRightIcon className="w-5 h-5 -ms-[22px]" />
                </Button>
            </div>

            <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {pagination.current_page} of {pagination.last_page}
            </span>
        </div>
    )


    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title + `${training?.name}`}
            maxWidth="6xl"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        variant="outline"
                        color="neutral"
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        type="submit"
                        form="assign-training-form"
                        disabled={!canSubmit}
                    >
                        {submitting ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="assign-training-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">

                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    {renderModeSelector()}

                    {/* Event/Area/Shift cascade selectors */}
                    {(assignmentMode === 'event' || assignmentMode === 'area' || assignmentMode === 'shift') && renderEventSelector()}
                    {(assignmentMode === 'area' || assignmentMode === 'shift') && renderAreaSelector()}
                    {assignmentMode === 'shift' && renderShiftSelector()}

                    {/* Duty selector */}
                    {assignmentMode === 'duty' && renderDutySelector()}

                    {/* Lead inclusion options */}
                    {renderLeadOptions()}

                    {/* Preview section */}
                    {renderPreviewSection()}

                    {/* Manual selection */}
                    {renderManualSelection()}

                    {loading && assignmentMode === 'manual' && (
                        <div className="text-center py-8">
                            <p className="text-gray-500 dark:text-gray-400">Loading volunteers...</p>
                        </div>
                    )}

                </div>
            </form>
        </Modal>
    )
}
